import { useRef, useState, type ReactNode } from 'react';
import { Link, createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Target } from '@bounded/api-client';
import { api } from '../../api/client';
import { q } from '../../api/queries';
import { useNames } from '../../api/names';
import { ConfirmationChip, ErrorNotice, Provenance } from '@bounded/ui/state';
import { Button, Empty, Mono, Panel, Select, Skeleton, Table, col, rowClass } from '@bounded/ui/primitives';
import { explain } from '@bounded/ui/errors';
import { useFormat, useT } from '../../i18n';

export const Route = createFileRoute('/_app/projects/$projectId/')({ component: Rollout });

/**
 * One idempotency key per (device, release, generation) intent, kept across retries of the same
 * rollout and replaced when any of those change. Batch rollout reuses the same keys as single rows.
 */
function useRolloutKeys() {
  const keys = useRef(new Map<string, string>());
  return (device: string, release: string, generation: number) => {
    const intent = `${device}|${release}|${generation}`;
    if (!keys.current.has(intent)) keys.current.set(intent, crypto.randomUUID());
    return keys.current.get(intent)!;
  };
}

function Rollout() {
  const t = useT(); const m = t.rollout; const f = useFormat();
  const { projectId } = Route.useParams();
  const queryClient = useQueryClient();
  const names = useNames();
  const draft = useQuery(q.draft(projectId));
  const releases = useQuery(q.releases(projectId));
  const targets = useQuery(q.targets(projectId));
  const devices = useQuery(q.devices());
  const keyFor = useRolloutKeys();
  const latest = releases.data?.items[0];
  const items = targets.data?.items ?? [];
  const behind = latest ? items.filter((x) => x.release_id !== latest.id) : [];
  const unassigned = (devices.data?.items ?? []).filter((d) => !d.revoked_at && !items.some((x) => x.device_id === d.id));
  const unpublished = draft.data && latest && (latest.draft_revision !== draft.data.revision || latest.defaults_revision !== draft.data.defaults_revision);

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['project', projectId] }).then(() => queryClient.invalidateQueries({ queryKey: ['overview'] }));
  // Each device is its own request: one conflict (e.g. a newer rollout queued elsewhere) must not hide the others' results.
  const rollAll = useMutation({
    mutationFn: async (release: string) => {
      const results = await Promise.allSettled(behind.map((x) => api.rollout(projectId, { device_id: x.device_id, release_id: release, expected_generation: x.generation }, keyFor(x.device_id, release, x.generation))));
      return results.map((r, i) => ({ device: behind[i].device_id, error: r.status === 'rejected' ? r.reason as unknown : null }));
    },
    onSettled: refresh,
  });
  const addDevice = useMutation({
    mutationFn: (device: string) => api.rollout(projectId, { device_id: device, release_id: latest!.id, expected_generation: 0 }, keyFor(device, latest!.id, 0)),
    onSettled: refresh,
  });
  const [adding, setAdding] = useState('');
  const failed = rollAll.data?.filter((r) => r.error) ?? [];

  return (
    <div className="grid gap-6">
      {/* The pipeline is a real sequence: a change only protects anything at the last step. */}
      <ol aria-label={m.pipeline} className="grid gap-px overflow-hidden rounded-[var(--radius-panel)] border border-rule bg-rule sm:grid-cols-3">
        <Stage n={1} title={m.draft} solid={false}>
          {draft.data ? m.draftState(draft.data.revision, unpublished ? 'changed' : latest ? 'same' : 'unpublished') : draft.error ? m.noPolicy : <Skeleton className="h-4 w-32" />}
          <Link to="/projects/$projectId/policy" params={{ projectId }} className="mt-2 block text-boundary hover:underline">{draft.error ? m.choosePolicy : m.editPolicy}</Link>
        </Stage>
        <Stage n={2} title={m.release} solid={false}>
          {latest ? m.releaseState(<Mono value={latest.id} />, f.ago(latest.created_at)) : m.nothingPublished}
        </Stage>
        <Stage n={3} title={m.onDevices} solid={items.length > 0 && items.every((x) => x.status === 'applied')}>
          {targets.data ? m.confirmed(items.filter((x) => x.status === 'applied').length, items.length) : <Skeleton className="h-4 w-32" />}
        </Stage>
      </ol>

      <Panel title={m.devicesTitle} description={m.devicesDescription} footer={<Provenance>{m.provenance}</Provenance>}
        actions={latest && behind.length > 1 && <Button size="sm" variant="primary" disabled={rollAll.isPending} onClick={() => rollAll.mutate(latest.id)}>{m.rollAll(behind.length)}</Button>}>
        {targets.isPending && <div className="p-4"><Skeleton className="h-20" /></div>}
        {targets.error && <div className="p-4"><ErrorNotice error={targets.error} onRetry={() => void targets.refetch()} /></div>}
        {failed.length > 0 && (
          <div role="alert" className="border-b border-rule px-4 py-3">
            <p className="font-medium text-deny">{m.notSent(f.list(failed.map((x) => names.device(x.device))))}</p>
            <ul className="mt-1 text-sm text-graphite">{failed.map((x) => <li key={x.device}>{names.device(x.device)}: {explain(x.error, t).title}</li>)}</ul>
          </div>
        )}
        {targets.data && items.length === 0 && <Empty title={m.emptyTitle}>{latest ? m.emptyWithRelease : m.emptyNoRelease}</Empty>}
        {items.length > 0 && (
          <Table minWidth="0" head={[{ label: m.cols.device, className: 'sm:w-[28%]' }, { label: m.cols.shouldRun, className: `w-[22%] ${col.hideSm}` }, { label: m.cols.reports }, { className: col.action }]}>
            {items.map((x) => <TargetRow key={x.device_id} target={x} latest={latest?.id} projectId={projectId} keyFor={keyFor} onDone={refresh} />)}
          </Table>
        )}
        {latest && unassigned.length > 0 && (
          <form className="flex flex-wrap items-center gap-2 border-t border-rule px-4 py-3" onSubmit={(e) => { e.preventDefault(); if (adding) addDevice.mutate(adding, { onSuccess: () => setAdding('') }); }}>
            <Select aria-label={m.addDeviceLabel} className="w-56" value={adding} onChange={(e) => setAdding(e.target.value)}>
              <option value="">{m.addDevice}</option>
              {unassigned.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
            </Select>
            <Button size="sm" type="submit" disabled={!adding || addDevice.isPending}>{m.sendLatest}</Button>
            {addDevice.error && <div className="basis-full"><ErrorNotice error={addDevice.error} onRefresh={() => void refresh()} /></div>}
          </form>
        )}
      </Panel>

      <Panel title={m.releases} description={m.releasesDescription}>
        {releases.data?.items.length === 0 && <Empty title={m.noReleases}>{m.noReleasesBody}</Empty>}
        <ul>{releases.data?.items.map((r, i) => (
          <li key={r.id} className={`flex flex-wrap items-center justify-between gap-x-4 gap-y-1 ${rowClass}`}>
            <span className="flex flex-wrap items-center gap-x-3">
              <Mono value={r.id} className="font-medium" />
              <span className="text-graphite">{m.releaseFrom(f.ago(r.created_at), r.draft_revision ?? null)}</span>
              {i === 0 && <span className="rounded bg-sunken px-1.5 text-sm">{t.common.latest}</span>}
            </span>
            <Mono value={r.artifact_hash} copy className="text-graphite" />
          </li>
        ))}</ul>
        {releases.data?.next_after && <p className="border-t border-rule px-4 py-3 text-sm text-graphite">{m.olderHidden}</p>}
      </Panel>
    </div>
  );
}

function Stage({ n, title, solid, children }: { n: number; title: string; solid: boolean; children: ReactNode }) {
  return (
    <li className="bg-surface p-4">
      <p className="flex items-center gap-2 font-medium"><span aria-hidden className={solid ? 'grid size-5 place-items-center rounded-full bg-ink text-xs text-paper' : 'grid size-5 place-items-center rounded-full border border-dashed border-graphite text-xs text-graphite'}>{n}</span>{title}</p>
      <div className="mt-1.5 text-graphite">{children}</div>
    </li>
  );
}

function TargetRow({ target, latest, projectId, keyFor, onDone }: { target: Target; latest?: string; projectId: string; keyFor: (d: string, r: string, g: number) => string; onDone: () => Promise<unknown> }) {
  const t = useT(); const m = t.rollout;
  const names = useNames();
  const rollout = useMutation({
    mutationFn: (release: string) => api.rollout(projectId, { device_id: target.device_id, release_id: release, expected_generation: target.generation }, keyFor(target.device_id, release, target.generation)),
    onSettled: onDone,
  });
  const behind = latest && target.release_id !== latest;
  return (
    <tr>
      <td>
        <Link to="/projects/$projectId/devices/$deviceId" params={{ projectId, deviceId: target.device_id }} className="font-medium hover:underline">{names.device(target.device_id)}</Link>
        {/* On narrow screens the release moves under the name so the status column stays visible. */}
        {target.release_id && <p className="mt-0.5 sm:hidden"><Mono value={target.release_id} className="text-graphite" /></p>}
      </td>
      <td className={col.hideSm}>{target.release_id ? <Mono value={target.release_id} /> : '—'}{target.release_id && target.release_id === latest && <span className="ml-2 text-sm text-graphite">{m.latest}</span>}</td>
      <td>
        {target.status === 'applied' && <ConfirmationChip state="confirmed">{m.running}</ConfirmationChip>}
        {target.status === 'pending' && <ConfirmationChip state="pending" title={m.lastApplied(target.observed_generation)}>{m.notConfirmed}</ConfirmationChip>}
        {target.status === 'rejected' && <ConfirmationChip state="rejected" title={target.reason_code ?? undefined}>{m.refused}</ConfirmationChip>}
        {target.status === 'rejected' && target.reason_code && <p className="mt-1 text-sm text-graphite">{m.reason(<code>{target.reason_code}</code>)}</p>}
        {target.status !== 'applied' && target.observed_generation > 0 && <p className="mt-1 text-sm text-graphite">{m.previous}</p>}
        {rollout.error && <div className="mt-2"><ErrorNotice error={rollout.error} onRefresh={() => void onDone()} /></div>}
      </td>
      <td className={col.action}>
        {behind && <Button size="sm" disabled={rollout.isPending} onClick={() => rollout.mutate(latest)}>{m.rollLatest}</Button>}
      </td>
    </tr>
  );
}
